import { useState, useEffect } from 'react';
import { 
    Clock, 
    Truck, 
    User,
    RefreshCw,
    Loader2,
    AlertTriangle,
    Edit,
    Calendar,
    Gauge,
    Activity,
    AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HOSPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updateDialog, setUpdateDialog] = useState({ open: false, truck: null });
    const [updating, setUpdating] = useState(false);
    const [formData, setFormData] = useState({
        drive_hours_remaining: 11,
        on_duty_hours_remaining: 14,
        cycle_hours_remaining: 70,
        next_reset_time: ''
    });

    useEffect(() => {
        fetchHOSData();
    }, []);

    const fetchHOSData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/hos-dashboard`, {
                credentials: 'include'
            });

            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                toast.error('Failed to load HOS data');
            }
        } catch (error) {
            console.error('Failed to fetch HOS data:', error);
            toast.error('Failed to load HOS data');
        } finally {
            setLoading(false);
        }
    };

    const openUpdateDialog = (truck) => {
        setFormData({
            drive_hours_remaining: truck.drive_hours_remaining ?? 11,
            on_duty_hours_remaining: truck.on_duty_hours_remaining ?? 14,
            cycle_hours_remaining: truck.cycle_hours_remaining ?? 70,
            next_reset_time: truck.next_reset_time ? truck.next_reset_time.slice(0, 16) : ''
        });
        setUpdateDialog({ open: true, truck });
    };

    const handleUpdateHOS = async () => {
        if (!updateDialog.truck) return;

        setUpdating(true);
        try {
            const payload = {
                drive_hours_remaining: parseFloat(formData.drive_hours_remaining),
                on_duty_hours_remaining: parseFloat(formData.on_duty_hours_remaining),
                cycle_hours_remaining: parseFloat(formData.cycle_hours_remaining),
                source: 'manual'
            };

            if (formData.next_reset_time) {
                payload.next_reset_time = new Date(formData.next_reset_time).toISOString();
            }

            // Use POST for upsert (handles both create and update)
            const response = await fetch(`${API_URL}/api/hos/${updateDialog.truck.truck_id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('HOS updated successfully');
                setUpdateDialog({ open: false, truck: null });
                fetchHOSData();
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to update HOS');
            }
        } catch (error) {
            console.error('Failed to update HOS:', error);
            toast.error('Failed to update HOS');
        } finally {
            setUpdating(false);
        }
    };

    // Color logic for drive hours
    const getDriveHoursColor = (hours) => {
        if (hours === null || hours === undefined) return 'text-zinc-500';
        if (hours >= 6) return 'text-green-400';
        if (hours >= 3) return 'text-amber-400';
        return 'text-red-400';
    };

    const getDriveHoursBg = (hours) => {
        if (hours === null || hours === undefined) return 'bg-zinc-500/20';
        if (hours >= 6) return 'bg-green-500/20';
        if (hours >= 3) return 'bg-amber-500/20';
        return 'bg-red-500/20';
    };

    // Color logic for cycle hours
    const getCycleHoursColor = (hours) => {
        if (hours === null || hours === undefined) return 'text-zinc-500';
        if (hours >= 20) return 'text-green-400';
        if (hours >= 10) return 'text-amber-400';
        return 'text-red-400';
    };

    const getCycleHoursBg = (hours) => {
        if (hours === null || hours === undefined) return 'bg-zinc-500/20';
        if (hours >= 20) return 'bg-green-500/20';
        if (hours >= 10) return 'bg-amber-500/20';
        return 'bg-red-500/20';
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return 'N/A';
        }
    };

    const formatHours = (hours) => {
        if (hours === null || hours === undefined) return '--';
        return hours.toFixed(1);
    };

    const hasWarnings = (truck) => {
        return truck.warnings?.low_drive_hours || 
               truck.warnings?.low_cycle_hours || 
               truck.warnings?.stale_data;
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="hos-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-3">
                        <Gauge className="w-6 h-6 text-orange-500" />
                        HOS Command
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Hours of Service status for all trucks • Sorted by risk level
                    </p>
                </div>
                <Button 
                    variant="outline"
                    onClick={fetchHOSData}
                    disabled={loading}
                    className="border-zinc-700"
                    data-testid="refresh-hos-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary Cards */}
            {data && (
                <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-zinc-700/50 rounded-sm flex items-center justify-center">
                                    <Truck className="w-5 h-5 text-zinc-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase">Total Trucks</p>
                                    <p className="text-2xl font-mono font-bold text-white">{data.total_trucks}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-green-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase">With HOS Data</p>
                                    <p className="text-2xl font-mono font-bold text-green-400">{data.trucks_with_hos}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                                    data.trucks_at_risk > 0 ? 'bg-red-500/20' : 'bg-green-500/20'
                                }`}>
                                    <AlertTriangle className={`w-5 h-5 ${
                                        data.trucks_at_risk > 0 ? 'text-red-400' : 'text-green-400'
                                    }`} />
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase">At Risk</p>
                                    <p className={`text-2xl font-mono font-bold ${
                                        data.trucks_at_risk > 0 ? 'text-red-400' : 'text-green-400'
                                    }`}>{data.trucks_at_risk}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Trucks List */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                    <p className="text-zinc-400">Loading HOS data...</p>
                </div>
            ) : !data || data.trucks.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Truck className="w-12 h-12 text-zinc-600 mb-4" />
                        <h3 className="text-lg font-medium text-white mb-2">No Trucks Found</h3>
                        <p className="text-zinc-400 text-center">
                            Add trucks from the Trucks page to track their HOS status.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hos-trucks-grid">
                    {data.trucks.map((truck) => (
                        <Card 
                            key={truck.truck_id} 
                            className={`bg-zinc-900/50 border-zinc-800 ${
                                hasWarnings(truck) ? 'border-l-4 border-l-red-500' : ''
                            }`}
                            data-testid={`hos-card-${truck.truck_id}`}
                        >
                            <CardContent className="p-4">
                                {/* Header Row */}
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-lg font-semibold text-white">
                                                {truck.truck_name}
                                            </h3>
                                            {hasWarnings(truck) && (
                                                <Badge className="bg-red-500/20 text-red-400 border-0" data-testid="warning-badge">
                                                    <AlertCircle className="w-3 h-3 mr-1" />
                                                    At Risk
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                                            <User className="w-4 h-4" />
                                            <span>{truck.driver_name || 'No driver assigned'}</span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openUpdateDialog(truck)}
                                        className="border-zinc-700"
                                        data-testid={`update-hos-btn-${truck.truck_id}`}
                                    >
                                        <Edit className="w-4 h-4 mr-1" />
                                        {truck.has_hos ? 'Update HOS' : 'Enter HOS Manually'}
                                    </Button>
                                </div>

                                {truck.has_hos ? (
                                    <>
                                        {/* HOS Metrics */}
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            {/* Drive Hours */}
                                            <div className={`${getDriveHoursBg(truck.drive_hours_remaining)} rounded-sm p-3`}>
                                                <p className="text-xs text-zinc-400 uppercase mb-1">Drive Hours</p>
                                                <p className={`text-2xl font-mono font-bold ${getDriveHoursColor(truck.drive_hours_remaining)}`}>
                                                    {formatHours(truck.drive_hours_remaining)}
                                                </p>
                                                {truck.warnings?.low_drive_hours && (
                                                    <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        Critical
                                                    </p>
                                                )}
                                            </div>

                                            {/* On Duty Hours */}
                                            <div className="bg-zinc-800/50 rounded-sm p-3">
                                                <p className="text-xs text-zinc-400 uppercase mb-1">On Duty</p>
                                                <p className="text-2xl font-mono font-bold text-white">
                                                    {formatHours(truck.on_duty_hours_remaining)}
                                                </p>
                                            </div>

                                            {/* Cycle Hours */}
                                            <div className={`${getCycleHoursBg(truck.cycle_hours_remaining)} rounded-sm p-3`}>
                                                <p className="text-xs text-zinc-400 uppercase mb-1">Cycle Hours</p>
                                                <p className={`text-2xl font-mono font-bold ${getCycleHoursColor(truck.cycle_hours_remaining)}`}>
                                                    {formatHours(truck.cycle_hours_remaining)}
                                                </p>
                                                {truck.warnings?.low_cycle_hours && (
                                                    <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        Low
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Metadata Row */}
                                        <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-zinc-800">
                                            <div className="flex items-center gap-4">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    Reset: {formatDateTime(truck.next_reset_time)}
                                                </span>
                                                <span>
                                                    Source: <span className={truck.source === 'eld_api' ? 'text-green-400' : 'text-amber-400'}>
                                                        {truck.source === 'eld_api' ? 'ELD' : 'Manual'}
                                                    </span>
                                                </span>
                                            </div>
                                            <div className={truck.warnings?.stale_data ? 'text-red-400' : ''}>
                                                {truck.warnings?.stale_data && (
                                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                )}
                                                Synced: {formatDateTime(truck.last_synced_at)}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    /* No HOS Data State */
                                    <div className="bg-zinc-800/30 rounded-sm p-6 text-center">
                                        <div className="w-12 h-12 bg-zinc-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <Clock className="w-6 h-6 text-zinc-500" />
                                        </div>
                                        <p className="text-zinc-400 mb-1">No HOS Data</p>
                                        <p className="text-xs text-zinc-500">
                                            Enter hours manually or connect ELD
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Update HOS Dialog */}
            <Dialog open={updateDialog.open} onOpenChange={(open) => !open && setUpdateDialog({ open: false, truck: null })}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Gauge className="w-5 h-5 text-orange-500" />
                            Update HOS
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {updateDialog.truck && (
                                <>Enter current HOS status for <span className="text-white font-medium">{updateDialog.truck.truck_name}</span></>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-zinc-400">Drive Hours Remaining</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="11"
                                    value={formData.drive_hours_remaining}
                                    onChange={(e) => setFormData({ ...formData, drive_hours_remaining: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    data-testid="drive-hours-input"
                                />
                            </div>
                            <div>
                                <Label className="text-zinc-400">On Duty Hours Remaining</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="14"
                                    value={formData.on_duty_hours_remaining}
                                    onChange={(e) => setFormData({ ...formData, on_duty_hours_remaining: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    data-testid="on-duty-hours-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-zinc-400">Cycle Hours Remaining</Label>
                            <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="70"
                                value={formData.cycle_hours_remaining}
                                onChange={(e) => setFormData({ ...formData, cycle_hours_remaining: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                data-testid="cycle-hours-input"
                            />
                        </div>

                        <div>
                            <Label className="text-zinc-400">Next Reset Time (Optional)</Label>
                            <Input
                                type="datetime-local"
                                value={formData.next_reset_time}
                                onChange={(e) => setFormData({ ...formData, next_reset_time: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                data-testid="next-reset-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setUpdateDialog({ open: false, truck: null })}
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpdateHOS}
                            disabled={updating}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="save-hos-btn"
                        >
                            {updating ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save HOS'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
